import React, { useState, useRef } from 'react';

interface ColorizeProps {}

const Colorize: React.FC<ColorizeProps> = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [colorizedUrl, setColorizedUrl] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.match(/^image\/(jpeg|png)$/)) {
      setError('Only JPEG and PNG images are supported');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File must be smaller than 5MB');
      return;
    }

    setSelectedFile(file);
    setError(null);
    setColorizedUrl(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      // Simulate file input change
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      if (fileInputRef.current) {
        fileInputRef.current.files = dataTransfer.files;
        handleFileSelect({ target: { files: dataTransfer.files } } as any);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleColorize = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setProgress(0);
    setError(null);

    const formData = new FormData();
    formData.append('image', selectedFile);

    try {
      // Simulate progress (2-3 minutes)
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 5, 90));
      }, 8000);

      const response = await fetch('/api/colorize', {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Colorization failed');
      }

      const data = await response.json();
      setColorizedUrl(data.colorized_url);
    } catch (err: any) {
      setError(err.message || 'An error occurred during colorization');
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handleDownload = () => {
    if (!colorizedUrl) return;

    const link = document.createElement('a');
    link.href = colorizedUrl;
    link.download = `colorized_${selectedFile?.name || 'image.png'}`;
    link.click();
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setColorizedUrl(null);
    setError(null);
    setProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div style={{ padding: '16px', fontFamily: 'MS Sans Serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <h3 style={{ marginTop: 0, marginBottom: '16px' }}>AI Image Colorization</h3>

      {/* File Upload Area */}
      {!selectedFile && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: '2px inset #808080',
            background: '#fff',
            padding: '40px',
            textAlign: 'center',
            cursor: 'pointer',
            marginBottom: '16px',
          }}
        >
          <p style={{ margin: 0, fontSize: '11px' }}>
            Drop image here or click to browse
          </p>
          <p style={{ margin: '8px 0 0', fontSize: '10px', color: '#808080' }}>
            JPEG or PNG, max 5MB
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* Image Preview/Comparison */}
      {selectedFile && (
        <div style={{ flex: 1, overflow: 'auto', marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {/* Original Image */}
            <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
              <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>
                Original
              </div>
              <div style={{ border: '2px inset #808080', background: '#000', padding: '4px' }}>
                <img
                  src={previewUrl || ''}
                  alt="Original"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </div>
            </div>

            {/* Colorized Image */}
            {colorizedUrl && (
              <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>
                  Colorized
                </div>
                <div style={{ border: '2px inset #808080', background: '#000', padding: '4px' }}>
                  <img
                    src={`${colorizedUrl}?t=${Date.now()}`}
                    alt="Colorized"
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                    onError={(e) => {
                      console.error('Failed to load colorized image:', colorizedUrl);
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {isProcessing && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', marginBottom: '4px' }}>
            Processing... (2-3 minutes)
          </div>
          <div style={{ border: '2px inset #808080', background: '#fff', height: '20px', padding: '2px' }}>
            <div
              style={{
                background: 'linear-gradient(to right, #000080 0%, #1084d0 100%)',
                height: '100%',
                width: `${progress}%`,
                transition: 'width 0.3s',
              }}
            />
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          style={{
            border: '2px outset #808080',
            background: '#c0c0c0',
            padding: '8px',
            marginBottom: '16px',
            fontSize: '11px',
            color: '#000',
          }}
        >
          ⚠️ {error}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
        {!colorizedUrl && selectedFile && (
          <button onClick={handleColorize} disabled={isProcessing}>
            {isProcessing ? 'Processing...' : 'Colorize'}
          </button>
        )}

        {colorizedUrl && (
          <>
            <button onClick={handleDownload}>Download Result</button>
            <button onClick={handleReset}>Colorize Another</button>
          </>
        )}

        {selectedFile && !isProcessing && !colorizedUrl && (
          <button onClick={handleReset}>Cancel</button>
        )}
      </div>
    </div>
  );
};

export default Colorize;
